import React from 'react';
import axios from 'axios';

// Generate a session ID for the browser session
const generateSessionId = () => {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

// Track page visit to server
const trackPageVisit = async (visitData) => {
  try {
    const sessionId = generateSessionId();
    
    const payload = {
      page_url: window.location.pathname + window.location.search,
      page_title: document.title,
      referrer: document.referrer,
      session_id: sessionId,
      additional_data: {
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height
        },
        timestamp: new Date().toISOString(),
        ...visitData
      }
    };

    // Get auth token if available
    const token = localStorage.getItem('studentToken') || localStorage.getItem('adminToken');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.post('/api/track/visit', payload, { headers });
    
    // Store visit ID for duration tracking
    if (response.data.visitId) {
      sessionStorage.setItem('currentVisitId', response.data.visitId);
      sessionStorage.setItem('visitStartTime', Date.now().toString());
    }
    
    return response.data;
  } catch (error) {
    console.error('Error tracking page visit:', error);
  }
};

// Update visit duration when leaving page
const updateVisitDuration = async () => {
  try {
    const visitId = sessionStorage.getItem('currentVisitId');
    const startTime = sessionStorage.getItem('visitStartTime');
    
    if (!visitId || !startTime) return;
    
    const duration = Math.floor((Date.now() - parseInt(startTime)) / 1000); // seconds
    
    if (duration > 0) {
      await axios.post('/api/track/visit/duration', {
        visit_id: parseInt(visitId),
        duration
      });
    }
    
    // Clear session storage
    sessionStorage.removeItem('currentVisitId');
    sessionStorage.removeItem('visitStartTime');
  } catch (error) {
    console.error('Error updating visit duration:', error);
  }
};

// Set up page visibility and beforeunload listeners
const setupVisitDurationTracking = () => {
  // Track when user leaves the page
  const handleBeforeUnload = () => {
    // Use sendBeacon for reliable tracking on page unload
    const visitId = sessionStorage.getItem('currentVisitId');
    const startTime = sessionStorage.getItem('visitStartTime');
    
    if (visitId && startTime) {
      const duration = Math.floor((Date.now() - parseInt(startTime)) / 1000);
      
      if (duration > 0 && navigator.sendBeacon) {
        const data = new FormData();
        data.append('visit_id', visitId);
        data.append('duration', duration);
        navigator.sendBeacon('/api/track/visit/duration', data);
      }
    }
  };
  
  // Track when page becomes hidden/visible (tab switching)
  const handleVisibilityChange = () => {
    if (document.hidden) {
      // Page became hidden - update duration
      updateVisitDuration();
    } else {
      // Page became visible - start new tracking
      const visitId = sessionStorage.getItem('currentVisitId');
      if (visitId) {
        sessionStorage.setItem('visitStartTime', Date.now().toString());
      }
    }
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

// Enhanced track activity that also includes visit context
const trackActivityWithVisit = async (activityData) => {
  try {
    // First ensure we have a visit record for this page
    await trackPageVisit({ activity_context: activityData.activity_type });
    
    // Then track the specific activity using existing system
    const { default: trackActivity } = await import('./tracking');
    return trackActivity(activityData);
  } catch (error) {
    console.error('Error tracking activity with visit:', error);
  }
};

// React Hook for automatic page tracking
const usePageTracking = (additionalData = {}) => {
  React.useEffect(() => {
    // Track the page visit
    trackPageVisit(additionalData);
    
    // Set up duration tracking
    const cleanup = setupVisitDurationTracking();
    
    // Cleanup on unmount
    return cleanup;
  }, []);
};

// Higher-order component for automatic page tracking
const withPageTracking = (WrappedComponent, additionalData = {}) => {
  return function PageTrackingWrapper(props) {
    usePageTracking(additionalData);
    return React.createElement(WrappedComponent, props);
  };
};

export {
  trackPageVisit,
  updateVisitDuration,
  trackActivityWithVisit,
  usePageTracking,
  withPageTracking,
  generateSessionId
};

export default trackPageVisit;